export const ONSITE_SIZE_PRESETS = {
  S: 320,
  M: 420,
  L: 560,
};

export const ONSITE_SIZE_MODES = {
  AUTO: 'AUTO',
  PRESET: 'PRESET',
  CUSTOM: 'CUSTOM',
};

/** Mobile breakpoint (px) used in responsiveMetrics and Auto sizing. */
export const MOBILE_BREAKPOINT = 480;

/**
 * Height stored on the mobile breakpoint itself.
 * This is the phone frame, not a copy of the desktop fallback size.
 */
export const ONSITE_MOBILE_HEIGHT = { value: 100, unit: 'PERCENT' };

function hasDimensionValue(dimension) {
  return dimension?.value != null && dimension.value !== '';
}

export function getMobileBreakpoint(responsiveMetrics) {
  if (!Array.isArray(responsiveMetrics)) return undefined;
  return responsiveMetrics.find(
    (item) => Number(item?.breakpointWidth?.value) === MOBILE_BREAKPOINT
  );
}

/** Write one dimension on the <=480 rule, keeping its other fields. */
export function setMobileBreakpointDimension(
  responsiveMetrics,
  key,
  dimension
) {
  const list = Array.isArray(responsiveMetrics)
    ? responsiveMetrics.map((item) => ({ ...item }))
    : [];
  const index = list.findIndex(
    (item) => Number(item?.breakpointWidth?.value) === MOBILE_BREAKPOINT
  );
  if (index === -1) {
    list.push({
      breakpointWidth: { value: MOBILE_BREAKPOINT, unit: 'PX' },
      [key]: dimension,
    });
    return list;
  }
  list[index] = { ...list[index], [key]: dimension };
  return list;
}

/** Keep each <=480 rule renderable: width plus its own height. */
export function completeMobileBreakpoint(responsiveMetrics) {
  if (!Array.isArray(responsiveMetrics)) return responsiveMetrics;
  return responsiveMetrics.map((item) => {
    if (Number(item?.breakpointWidth?.value) !== MOBILE_BREAKPOINT) return item;
    if (hasDimensionValue(item?.height)) return item;
    return {
      ...item,
      height: { ...ONSITE_MOBILE_HEIGHT },
    };
  });
}

/** Desktop Auto width — modal-sized, not full viewport */
export const ONSITE_AUTO_DESKTOP_WIDTH_PX = ONSITE_SIZE_PRESETS.M;

export function buildAutoMetrics() {
  return {
    fallbackMetric: {
      width: { value: ONSITE_AUTO_DESKTOP_WIDTH_PX, unit: 'PX' },
    },
    responsiveMetrics: completeMobileBreakpoint([
      {
        breakpointWidth: { value: MOBILE_BREAKPOINT, unit: 'PX' },
        width: { value: 100, unit: 'PERCENT' },
      },
    ]),
  };
}

export function buildPresetMetrics(preset) {
  const widthPx = ONSITE_SIZE_PRESETS[preset] ?? ONSITE_SIZE_PRESETS.M;
  return {
    fallbackMetric: {
      width: { value: widthPx, unit: 'PX' },
    },
    responsiveMetrics: completeMobileBreakpoint([
      {
        breakpointWidth: { value: MOBILE_BREAKPOINT, unit: 'PX' },
        width: { value: widthPx, unit: 'PX' },
      },
    ]),
  };
}

/** Empty Custom fields (PX units only, no numeric values). */
export function buildEmptyCustomMetrics() {
  return {
    fallbackMetric: {
      width: { unit: 'PX' },
      height: { unit: 'PX' },
    },
    responsiveMetrics: [],
  };
}

function isAutoShapedMetric(fallbackMetric) {
  if (!fallbackMetric?.width) return false;
  const { value, unit } = fallbackMetric.width;
  // Legacy Auto was stored as 100% width. Do NOT treat 420px as Auto — Custom
  // users can set width to the same PX value as the Auto desktop default.
  return unit === 'PERCENT' && Number(value) === 100;
}

/**
 * Desktop width that is clearly Custom (not legacy 100% Auto, not the 420px
 * Auto desktop default). Used so leftover Auto mobile rules (480px → 100%)
 * cannot override a custom campaign.
 */
function hasCustomDesktopWidth(fallbackMetric) {
  if (!fallbackMetric?.width) return false;
  if (isAutoShapedMetric(fallbackMetric)) return false;
  const { value, unit } = fallbackMetric.width;
  if (value == null || value === '') return false;
  const widthUnit = unit || 'PX';
  if (widthUnit !== 'PX') return true;
  return Number(value) !== ONSITE_AUTO_DESKTOP_WIDTH_PX;
}

function looksLikeMaterializedAutoFallback(fallbackMetric) {
  const widthValue = fallbackMetric?.width?.value;
  const widthUnit = fallbackMetric?.width?.unit;
  const heightValue = fallbackMetric?.height?.value;
  return (
    widthUnit === 'PX' &&
    Number(widthValue) === ONSITE_AUTO_DESKTOP_WIDTH_PX &&
    (heightValue == null || heightValue === '')
  );
}

function stripLingeringHeight(config) {
  if (config.fallbackMetric && 'height' in config.fallbackMetric) {
    const restMetric = { ...config.fallbackMetric };
    delete restMetric.height;
    config.fallbackMetric = restMetric;
  }
}

/** Check if responsiveMetrics contains the Auto mobile rule (480px -> 100%). */
export function hasAutoResponsiveMetrics(responsiveMetrics) {
  if (!Array.isArray(responsiveMetrics) || responsiveMetrics.length === 0) {
    return false;
  }
  return responsiveMetrics.some((item) => {
    const bp = Number(item?.breakpointWidth?.value);
    const widthVal = Number(item?.width?.value);
    const widthUnit = item?.width?.unit;
    return (
      bp === MOBILE_BREAKPOINT && widthUnit === 'PERCENT' && widthVal === 100
    );
  });
}

/** Check if responsiveMetrics and fallbackMetric match a Preset rule (480px -> matching PX width). */
export function hasPresetResponsiveMetrics(responsiveMetrics, fallbackMetric) {
  if (!Array.isArray(responsiveMetrics) || responsiveMetrics.length === 0) {
    return false;
  }
  const fallbackWidth = Number(fallbackMetric?.width?.value);
  const fallbackUnit = fallbackMetric?.width?.unit || 'PX';
  if (fallbackUnit !== 'PX' || !fallbackWidth) return false;

  const isPresetWidth =
    Object.values(ONSITE_SIZE_PRESETS).includes(fallbackWidth);
  if (!isPresetWidth) return false;

  return responsiveMetrics.some((item) => {
    const bp = Number(item?.breakpointWidth?.value);
    const widthVal = Number(item?.width?.value);
    const widthUnit = item?.width?.unit || 'PX';
    return (
      bp === MOBILE_BREAKPOINT &&
      widthUnit === 'PX' &&
      widthVal === fallbackWidth
    );
  });
}

/**
 * Use API/saved configuration as-is. Never deep-merge emptyVariation Auto
 * defaults into it — that injected sizeMode:AUTO over legacy campaigns that
 * only had fallbackMetric and wiped Custom UI after save/reload/duplicate.
 */
export function takeSavedOnSiteConfiguration(apiConfiguration) {
  if (!apiConfiguration || typeof apiConfiguration !== 'object') {
    return getDefaultOnSiteConfiguration();
  }
  const config = { ...apiConfiguration };
  const hasAutoRules = hasAutoResponsiveMetrics(config.responsiveMetrics);
  const autoShaped = isAutoShapedMetric(config.fallbackMetric);

  // 1. Auto: genuine Auto only — leftover 480px→100% rules must not win when
  // the desktop fallback is a custom width (e.g. 640px). Those configs need
  // healing so Custom height and size mode survive reload/duplicate.
  if (
    autoShaped ||
    (hasAutoRules && !hasCustomDesktopWidth(config.fallbackMetric))
  ) {
    config.sizeMode = ONSITE_SIZE_MODES.AUTO;
    stripLingeringHeight(config);
    config.responsiveMetrics = completeMobileBreakpoint(
      config.responsiveMetrics
    );
    return config;
  }

  // 2. Preset: If Preset responsive metrics are present, ensure sizeMode is PRESET,
  // infer sizePreset, and strip lingering custom height.
  if (
    hasPresetResponsiveMetrics(config.responsiveMetrics, config.fallbackMetric)
  ) {
    config.sizeMode = ONSITE_SIZE_MODES.PRESET;
    config.sizePreset = getEffectiveSizePreset(config);
    stripLingeringHeight(config);
    config.responsiveMetrics = completeMobileBreakpoint(
      config.responsiveMetrics
    );
    return config;
  }

  // 3. Heal configs corrupted by the Auto-default merge bug: sizeMode AUTO
  // and/or injected Auto mobile rules, but metrics clearly look like Custom
  // (non-auto width and/or an explicit height).
  if (
    (config.sizeMode === ONSITE_SIZE_MODES.AUTO || hasAutoRules) &&
    config.fallbackMetric &&
    !autoShaped
  ) {
    if (!looksLikeMaterializedAutoFallback(config.fallbackMetric)) {
      config.sizeMode = ONSITE_SIZE_MODES.CUSTOM;
      if (hasAutoRules) {
        config.responsiveMetrics = [];
      }
    }
  }

  return config;
}

/**
 * Resolve effective size mode:
 * 1. Explicit sizeMode (AUTO, PRESET, CUSTOM)
 * 2. Inferred from responsiveMetrics / fallbackMetric invariants (when sizeMode unpersisted)
 * 3. FallbackMetric present → CUSTOM
 * 4. Otherwise → AUTO
 */
export function getEffectiveSizeMode(configuration) {
  if (
    configuration?.sizeMode === ONSITE_SIZE_MODES.AUTO ||
    configuration?.sizeMode === ONSITE_SIZE_MODES.PRESET ||
    configuration?.sizeMode === ONSITE_SIZE_MODES.CUSTOM
  ) {
    return configuration.sizeMode;
  }
  if (
    hasAutoResponsiveMetrics(configuration?.responsiveMetrics) &&
    !hasCustomDesktopWidth(configuration?.fallbackMetric)
  ) {
    return ONSITE_SIZE_MODES.AUTO;
  }
  if (
    hasPresetResponsiveMetrics(
      configuration?.responsiveMetrics,
      configuration?.fallbackMetric
    )
  ) {
    return ONSITE_SIZE_MODES.PRESET;
  }
  if (configuration?.fallbackMetric) {
    // Legacy 100%-width Auto metrics should still read as Auto in the UI.
    if (isAutoShapedMetric(configuration.fallbackMetric)) {
      return ONSITE_SIZE_MODES.AUTO;
    }
    return ONSITE_SIZE_MODES.CUSTOM;
  }
  return ONSITE_SIZE_MODES.AUTO;
}

export function getEffectiveSizePreset(configuration) {
  const preset = configuration?.sizePreset;
  if (preset && ONSITE_SIZE_PRESETS[preset] != null) {
    return preset;
  }
  const widthVal = Number(configuration?.fallbackMetric?.width?.value);
  const matchedEntry = Object.entries(ONSITE_SIZE_PRESETS).find(
    ([, px]) => px === widthVal
  );
  if (matchedEntry) {
    return matchedEntry[0];
  }
  return 'M';
}

/**
 * Normalize sizeMode/sizePreset into a configuration patch that includes
 * materialized fallbackMetric / responsiveMetrics for SDK compatibility.
 *
 * @param {object} args
 * @param {string} [args.previousSizeMode] - mode before this change (needed so
 *   CUSTOM does not inherit Auto's 100%/420 materialized values into the inputs)
 */
export function resolveOnSiteSizeConfig({
  sizeMode,
  sizePreset,
  fallbackMetric,
  responsiveMetrics,
  previousSizeMode,
} = {}) {
  const mode = sizeMode || ONSITE_SIZE_MODES.AUTO;

  if (mode === ONSITE_SIZE_MODES.AUTO) {
    const auto = buildAutoMetrics();
    return {
      sizeMode: ONSITE_SIZE_MODES.AUTO,
      sizePreset: undefined,
      fallbackMetric: auto.fallbackMetric,
      responsiveMetrics: auto.responsiveMetrics,
    };
  }

  if (mode === ONSITE_SIZE_MODES.PRESET) {
    const preset =
      sizePreset && ONSITE_SIZE_PRESETS[sizePreset] != null ? sizePreset : 'M';
    const built = buildPresetMetrics(preset);
    return {
      sizeMode: ONSITE_SIZE_MODES.PRESET,
      sizePreset: preset,
      fallbackMetric: built.fallbackMetric,
      responsiveMetrics: built.responsiveMetrics,
    };
  }

  // CUSTOM
  // Only clear inputs when the user explicitly switched Auto → Custom.
  // Do not infer "coming from Auto" from metric shape (e.g. width 420) — that
  // erased real Custom values after a bad sizeMode:AUTO injection on reload.
  const comingFromAuto = previousSizeMode === ONSITE_SIZE_MODES.AUTO;

  if (comingFromAuto) {
    const empty = buildEmptyCustomMetrics();
    return {
      sizeMode: ONSITE_SIZE_MODES.CUSTOM,
      sizePreset: undefined,
      fallbackMetric: empty.fallbackMetric,
      responsiveMetrics: empty.responsiveMetrics,
    };
  }

  // From Preset: keep the PX width as a starting point, clear height / auto responsive
  if (previousSizeMode === ONSITE_SIZE_MODES.PRESET && fallbackMetric?.width) {
    return {
      sizeMode: ONSITE_SIZE_MODES.CUSTOM,
      sizePreset: undefined,
      fallbackMetric: {
        width: {
          value: fallbackMetric.width.value,
          unit: fallbackMetric.width.unit || 'PX',
        },
        height: { unit: 'PX' },
      },
      responsiveMetrics: [],
    };
  }

  // Already custom (or legacy with metrics) — keep what the user set
  const result = {
    sizeMode: ONSITE_SIZE_MODES.CUSTOM,
    sizePreset: undefined,
  };
  if (fallbackMetric) {
    result.fallbackMetric = fallbackMetric;
  } else {
    result.fallbackMetric = buildEmptyCustomMetrics().fallbackMetric;
  }
  if (responsiveMetrics) {
    result.responsiveMetrics = responsiveMetrics;
  } else {
    result.responsiveMetrics = [];
  }
  return result;
}

export function getDefaultOnSiteConfiguration() {
  return {
    position: 'CENTER',
    ...resolveOnSiteSizeConfig({ sizeMode: ONSITE_SIZE_MODES.AUTO }),
  };
}

/**
 * Resolve iframe size for the mobile phone mockup screen area.
 * Preset/custom PX widths are capped to the screen; Auto uses full screen width.
 */
export function getMobilePreviewSize(configuration, screenWidthPx = 368) {
  const sizeMode = getEffectiveSizeMode(configuration);
  const metricsSource =
    configuration?.responsiveMetrics?.find(
      (item) => Number(item.breakpointWidth?.value) === MOBILE_BREAKPOINT
    ) || configuration?.fallbackMetric;

  const widthValue =
    metricsSource?.width?.value ?? configuration?.fallbackMetric?.width?.value;
  const widthUnit =
    metricsSource?.width?.unit ?? configuration?.fallbackMetric?.width?.unit;
  const heightValue =
    metricsSource?.height?.value ??
    configuration?.fallbackMetric?.height?.value;
  const heightUnit =
    metricsSource?.height?.unit ?? configuration?.fallbackMetric?.height?.unit;

  const treatAsAuto = sizeMode === ONSITE_SIZE_MODES.AUTO;

  let width = '100%';
  let maxWidth = '100%';

  if (treatAsAuto) {
    width = '100%';
    maxWidth = '100%';
  } else if (widthValue != null && widthValue !== '') {
    if (widthUnit === 'PERCENT') {
      const pct = Math.min(Number(widthValue), 100);
      width = `${pct}%`;
      maxWidth = '100%';
    } else {
      const capped = Math.min(Number(widthValue), screenWidthPx);
      width = `${capped}px`;
      maxWidth = '100%';
    }
  } else {
    width = '100%';
    maxWidth = '100%';
  }

  const height =
    sizeMode !== ONSITE_SIZE_MODES.CUSTOM
      ? 'auto'
      : heightValue != null && heightValue !== ''
      ? `${heightValue}${heightUnit === 'PERCENT' ? '%' : 'px'}`
      : 'auto';

  return { width, maxWidth, height };
}

/**
 * Resolve CSS size for desktop preview iframe.
 * Auto / percent widths are capped so the preview is not full-bleed.
 */
export function getDesktopPreviewSize(configuration) {
  const sizeMode = getEffectiveSizeMode(configuration);
  const widthValue = configuration?.fallbackMetric?.width?.value;
  const widthUnit = configuration?.fallbackMetric?.width?.unit;
  const heightValue = configuration?.fallbackMetric?.height?.value;
  const heightUnit = configuration?.fallbackMetric?.height?.unit;

  const treatAsAuto = sizeMode === ONSITE_SIZE_MODES.AUTO;

  let width = '100%';
  let maxWidth = '100%';

  if (treatAsAuto) {
    width = `${ONSITE_AUTO_DESKTOP_WIDTH_PX}px`;
    maxWidth = `${ONSITE_AUTO_DESKTOP_WIDTH_PX}px`;
  } else if (widthValue != null && widthValue !== '') {
    if (widthUnit === 'PERCENT') {
      // Percent of preview chrome — cap so it does not dominate the mock desktop
      const pct = Math.min(Number(widthValue), 100);
      width = `${pct}%`;
      maxWidth = `${ONSITE_SIZE_PRESETS.L}px`;
    } else {
      width = `${widthValue}px`;
      maxWidth = width;
    }
  } else {
    width = `${ONSITE_AUTO_DESKTOP_WIDTH_PX}px`;
    maxWidth = `${ONSITE_AUTO_DESKTOP_WIDTH_PX}px`;
  }

  const height =
    sizeMode !== ONSITE_SIZE_MODES.CUSTOM
      ? 'auto'
      : heightValue != null && heightValue !== ''
      ? `${heightValue}${heightUnit === 'PERCENT' ? '%' : 'px'}`
      : 'auto';

  return { width, maxWidth, height };
}

/**
 * CSS applied in dashboard OnSite preview so Auto/Preset/Custom widths
 * match SDK metrics. No !important — Advanced Styling !important still wins
 * (same cascade as a typical SDK inline/style application).
 */
export function buildOnSiteSizePreviewCss(configuration = {}) {
  const mode = getEffectiveSizeMode(configuration);
  const fallback = configuration?.fallbackMetric;
  const mobileRule =
    configuration?.responsiveMetrics?.find(
      (item) => Number(item.breakpointWidth?.value) === MOBILE_BREAKPOINT
    ) || null;

  const toWidth = (metric) => {
    const value = metric?.width?.value;
    const unit = metric?.width?.unit;
    if (value == null || value === '') return null;
    if (unit === 'PERCENT') return `${Number(value)}%`;
    return `${Number(value)}px`;
  };

  let desktopWidth = toWidth(fallback) || `${ONSITE_AUTO_DESKTOP_WIDTH_PX}px`;
  let mobileWidth = toWidth(mobileRule);

  if (mode === ONSITE_SIZE_MODES.AUTO) {
    desktopWidth = `${ONSITE_AUTO_DESKTOP_WIDTH_PX}px`;
    mobileWidth = mobileWidth || '100%';
  } else if (mode === ONSITE_SIZE_MODES.PRESET) {
    const preset = getEffectiveSizePreset(configuration);
    const px = ONSITE_SIZE_PRESETS[preset] ?? ONSITE_SIZE_PRESETS.M;
    desktopWidth = `${px}px`;
    mobileWidth = mobileWidth || `${px}px`;
  } else {
    desktopWidth = toWidth(fallback) || `${ONSITE_AUTO_DESKTOP_WIDTH_PX}px`;
    mobileWidth = mobileWidth || desktopWidth;
  }

  const desktopMax = desktopWidth.endsWith('%')
    ? '100%'
    : `min(${desktopWidth}, calc(100% - 24px))`;

  const selectors =
    '.onsite-text-layout, .onsite-image-layout, .onsite-coupon-layout';

  // left/right + margin centers fixed/absolute cards when width < viewport
  // (same pattern as gallery templates). Does not force position.
  return `
${selectors} {
  left: 0;
  right: 0;
  width: ${desktopWidth};
  max-width: ${desktopMax};
  margin-inline: auto;
  box-sizing: border-box;
}
@media (max-width: ${MOBILE_BREAKPOINT}px) {
  ${selectors} {
    width: ${mobileWidth};
    max-width: 100%;
  }
}
`;
}
